/**
 * Push-subscription self-heal for the gate PWA (Leg 7, J-0185 — push-wake reliability).
 *
 * iOS Web Push subscriptions are fragile. A subscription can be silently dropped after an OS
 * restart, after the push service rotates it, or while the app sat behind Focus Mode — and the
 * notification permission can be revoked from Settings at any time. If the phone has no LIVE,
 * relay-registered subscription at the moment a credential request lands, the doorbell push
 * never arrives and the operator is never woken. That is the exact failure this leg closes.
 *
 * So on every app open we RECONCILE the per-install subscription state and self-heal:
 *   - absent  (granted but no live subscription — lost after Focus/restart, or expired)
 *               → silently re-subscribe (no user gesture needed: permission is already granted),
 *   - stale   (a live subscription whose endpoint differs from what we last registered)
 *               → re-register it with the relay + persist the new endpoint,
 *   - valid   (live subscription matches the registered one) → do nothing,
 *   - denied  (permission revoked) → surface a clear "re-enable" prompt (needs a gesture),
 *   - default (never asked) → surface a prompt to enable.
 *
 * The reconciliation DECISION is a pure function (`pushDecision`) so the self-heal logic is
 * unit-testable without a browser. The DOM / ServiceWorker glue around it (`syncPush` driven by
 * an injected {@link PushEnv}) is deliberately thin.
 */

export type PushPermission = "granted" | "denied" | "default";

/** A snapshot of this install's push state, read once at app open. */
export interface PushState {
  /** Push APIs (Notification + serviceWorker + PushManager) available in this context. */
  supported: boolean;
  /** Notification.permission at app open. */
  permission: PushPermission;
  /** Endpoint of the live subscription (pushManager.getSubscription()), or null if none. */
  currentEndpoint: string | null;
  /** Endpoint we last registered with the relay + persisted for THIS install, or null. */
  registeredEndpoint: string | null;
}

export type PushAction =
  | "subscribe" //          absent: granted but no live subscription → create one silently
  | "resubscribe" //        stale: live subscription differs from the registered one → re-register
  | "noop" //               valid: live subscription matches the registered one
  | "prompt-reenable" //    permission revoked → clear UI prompt to re-enable (needs a gesture)
  | "request-permission" // never asked → needs a user gesture to enable
  | "unsupported"; //       push unavailable in this browser/context

/**
 * Decide what to do to converge on a live, relay-registered push subscription. Pure: inputs →
 * action, no side effects. The thin glue ({@link syncPush}) reads the real state, calls this,
 * and performs the action.
 *
 * Order matters: a revoked/absent permission is reported BEFORE any endpoint check, because a
 * stale endpoint is moot if we can't (re)subscribe without the operator first re-granting.
 */
export function pushDecision(s: PushState): PushAction {
  if (!s.supported) return "unsupported";
  if (s.permission === "denied") return "prompt-reenable";
  if (s.permission === "default") return "request-permission";
  // permission === "granted" from here on — we can act without a user gesture:
  if (s.currentEndpoint === null) return "subscribe"; // lost after Focus/restart, or expired
  if (s.currentEndpoint !== s.registeredEndpoint) return "resubscribe"; // rotated / never registered
  return "noop";
}

/** True when the action is silent (nothing for the operator to act on). */
export function isSilentAction(a: PushAction): boolean {
  return a === "subscribe" || a === "resubscribe" || a === "noop" || a === "unsupported";
}

/** True when the action needs the operator to (re)grant permission via a gesture. */
export function needsOperatorPrompt(a: PushAction): boolean {
  return a === "prompt-reenable" || a === "request-permission";
}

/**
 * The side-effecting capabilities `syncPush` needs, injected so the orchestration is testable
 * without a DOM/ServiceWorker. The browser implementation lives in app.ts.
 */
export interface PushEnv {
  /** Read the live push state for this install. */
  getState(): Promise<PushState>;
  /** Create a fresh subscription; returns its endpoint, or null on failure/denied. */
  subscribe(): Promise<string | null>;
  /** POST the current live subscription to the relay; returns true on success. */
  register(): Promise<boolean>;
  /** Persist the relay-registered endpoint for THIS install. */
  persist(endpoint: string): void;
  /** Surface a UI prompt asking the operator to (re)enable notifications. */
  onPrompt(action: "prompt-reenable" | "request-permission"): void;
}

/**
 * The outcome of one reconcile pass: the DECIDED `action` plus whether the wake channel is
 * actually believed live afterwards (`ok`). `ok` is true only when the heal ACHIEVED its goal
 * (subscribe/resubscribe that registered with the relay, or a noop on an already-valid
 * subscription) — never merely because a heal was attempted. The UI must gate any
 * "notifications on" indication on `ok`, not on the action (a subscribe whose relay
 * registration failed would otherwise read as success while the doorbell is dead → E826).
 */
export interface SyncPushResult {
  action: PushAction;
  ok: boolean;
}

/**
 * Reconcile + self-heal the push subscription once. Returns the action taken AND whether it
 * actually succeeded so callers can reflect true status in the UI. Best-effort: a failed
 * subscribe/register leaves the registered endpoint untouched so the NEXT open retries,
 * rather than persisting an unconfirmed endpoint.
 */
export async function syncPush(env: PushEnv): Promise<SyncPushResult> {
  const state = await env.getState();
  const action = pushDecision(state);
  switch (action) {
    case "subscribe": {
      const endpoint = await env.subscribe();
      if (endpoint && (await env.register())) {
        env.persist(endpoint);
        return { action, ok: true };
      }
      return { action, ok: false };
    }
    case "resubscribe": {
      // A live subscription already exists (currentEndpoint is non-null here); re-register it.
      if (state.currentEndpoint && (await env.register())) {
        env.persist(state.currentEndpoint);
        return { action, ok: true };
      }
      return { action, ok: false };
    }
    case "prompt-reenable":
    case "request-permission":
      env.onPrompt(action);
      return { action, ok: false };
    case "noop":
      return { action, ok: true }; // already valid + registered
    case "unsupported":
      return { action, ok: false };
  }
}
